portfolio uses current weights as starting weights.
portfolio is rebalanced to those weights at the specified rebalance frequency.
coverage includes stocks and funds
for securities with incomplete price histories, missing segments are estimated based on the most similar securities with at least 20 years of history.

<!-- StressTestMethodology.vue -->
<template>
  <div>
    <!-- Long-form version -->
    <div v-if="displayType === 'long'">
      <p>
        Historical stress tests show how a portfolio would have behaved over a historical window by replaying month-end market returns for the securities held with a specified rebalance frequency. 
        Historical simulations begin from the current allocation, where starting weights are the portfolio's current dollar weights.
        These results are historical in nature and are not guarantees of future results.
      </p>

      <p>
        Month-end returns are constructed from daily total-return series (adjusted for splits and dividends) and then aggregated to month ends. 
        The simulation applies {{ rebalanceLabel }} with dividend and capital-gain reinvestment.
        Cash is assumed to earn 0% monthly.
        Returns do not account for taxes, loads, sales charges, trading commissions, short-term trading fees, management fees, or advisory fees.
      </p>

      <p>
        Coverage and Returns Imputing:
        Returns coverage includes Equities, Mutual Funds, ETFs, and Cash.
        Securities with no usable history across the entire window are excluded and the remaining positions are renormalized at the starting point.
        Securities with incomplete price histories have missing segments estimated based on the behavior of similar securities with at least 20 years of history.
        We impute daily simple returns for securities with incomplete histories by fitting each security's observed returns to a non-negative linear combination of equities/ETFs with at least 20 years of history and near-complete coverage. 
        For a target security, we estimate NNLS coefficients on the overlapping window and then generate predictions on missing dates.
        To preserve realistic variation, we add randomly resampled in-sample residuals to the predicted values on missing dates.
        The imputed daily returns are subsequently aggregated to month-end in our reporting pipeline (outside this step).
      </p>

      <!-- Overall coverage (mirrors Returns page) -->
      <p>
        <span v-if="portfolio">
          Stress test results cover
          {{ portfolio.trailing_returns_monthly_rebalance?.[2]?.toLocaleString('en-US', {style: 'percent'}) || '—' }}
          of {{ portfolioName }}.
        </span>
        <span v-if="benchmark">
          &nbsp;Stress test results cover
          {{ benchmark.trailing_returns_monthly_rebalance?.[2]?.toLocaleString('en-US', {style: 'percent'}) || '—'  }}
          of {{ benchmarkName }}.
        </span>
        <span v-if="optimizedPortfolio">
          &nbsp;Stress test results cover
          {{ optimizedPortfolio.trailing_returns_monthly_rebalance?.[2]?.toLocaleString('en-US', {style: 'percent'}) || '—' }}
          of {{ optimizedPortfolioName }}.
        </span>
      </p>

      <p>Monthly return construction. Daily simple returns for each covered security are compounded
        within each calendar month to produce a monthly simple return:</p>
      <p><em>R<sub>i,m</sub> = ∏<sub>d∈m</sub> (1 + r<sub>i,d</sub>) - 1</em></p>

      <p>
        Portfolio return and rebalancing. Let <em>w<sub>i,m</sub></em> be the portfolio weight (after any
        scheduled rebalance) of asset <em>i</em> at month <em>m</em>, and <em>R<sub>i,m</sub></em> its monthly return. The portfolio's
        month-<em>m</em> return is:
      </p>
      <p><em>R<sub>p,m</sub> = Σ<sub>i=1</sub><sup>k</sup> w<sub>i,m</sub> · R<sub>i,m</sub></em></p>

      <p>
        Portfolio value is tracked from an initial value of 1.0.
        The total return over the window is <em>PV<sub>final</sub> - 1</em>.
      </p>

      <p>
        Important limitations. Historical stress tests are descriptive, not predictive. 
        They reflect an approximation based on available return histories and chosen rebalancing/weight assumptions. 
        Results may differ from actual investor experience due to fees, taxes, trading frictions, timing differences, data gaps, and corporate actions not captured by data providers.
      </p>
    </div>

    <!-- Short / summary version -->
    <p v-else>
      Stress tests replay month-end market returns over a historical window for covered portfolio holdings (equities, funds, ETFs, and cash), using {{ rebalanceLabel.toLowerCase() }} and the current allocation of the portfolio as the starting weights for each holding.
      Returns are built from daily total-return series aggregated to month ends, with dividends reinvested and cash assumed to earn 0%.
      Fees, taxes, and transaction costs are excluded. Missing segments for securities with incomplete price histories are estimated based on the behavior of similar securities.
      <span v-if="portfolio">
        Coverage for {{ portfolioName.length > 50 ? portfolioName.slice(0, 50) + '…' : portfolioName }}: {{ portfolio.trailing_returns_monthly_rebalance?.[2]?.toLocaleString('en-US', {style: 'percent'}) || '—' }}.
      </span>
      <span v-if="benchmark">
        &nbsp;Coverage for {{ benchmarkName.length > 50 ? benchmarkName.slice(0, 50) + '…' : benchmarkName }}: {{ benchmark.trailing_returns_monthly_rebalance?.[2]?.toLocaleString('en-US', {style: 'percent'}) || '—' }}.
      </span>
      <span v-if="optimizedPortfolio">
        &nbsp;Coverage for {{ optimizedPortfolioName.length > 50 ? optimizedPortfolioName.slice(0, 50) + '…' : optimizedPortfolioName }}: {{ optimizedPortfolio.trailing_returns_monthly_rebalance?.[2]?.toLocaleString('en-US', {style: 'percent'}) || '—' }}.
      </span>
      See full methodology below.
    </p>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';

export default {
  name: 'StressTestMethodology',
  props: {
    displayType: { type: String, default: 'long' }, // 'long' or anything else for short
    rebalance: { type: String, default: 'annual' }, // 'none' | 'annual' | 'monthly'

    // NEW: mirror Returns page inputs so we can read trailing_returns_monthly_rebalance coverage
    portfolio: { type: Object, default: null },
    benchmark: { type: Object, default: null },
    optimizedPortfolio: { type: Object, default: null },
  },
  mounted() {
    this.registerMethodologyData();
  },
  updated() {
    this.registerMethodologyData();
  },
  computed: {
    ...mapStores(useAdvisorStore, useCheckupStore),
    rebalanceLabel() {
      if (this.rebalance === 'monthly') return 'Monthly rebalancing';
      if (this.rebalance === 'none') return 'No rebalancing';
      return 'Annual rebalancing (year end)';
    },
    portfolioName() { return this.portfolio?.name || 'Portfolio'; },
    benchmarkName() { return this.benchmark?.name || 'Benchmark'; },
    optimizedPortfolioName() { return this.optimizedPortfolio?.name || 'Optimized Portfolio'; },
  },
  methods: {
    formatDate(d) {
      const dt = new Date(d);
      if (isNaN(dt)) return String(d);
      const mm = (dt.getMonth() + 1).toString().padStart(2, '0');
      const dd = dt.getDate().toString().padStart(2, '0');
      const yy = dt.getFullYear();
      return `${mm}/${dd}/${yy}`;
    },
    registerMethodologyData() {
      if (!this.$el || !this.displayType) return;

      const textContent = this.$el.textContent;

      // Only update if we have content and it's not just placeholder dashes
      if (textContent && !textContent.includes('— of') && !textContent.includes(': —,')) {
        const methodologyText = { [this.displayType]: textContent };
        this.checkupStore.setMethodologyData('StressTestMethodology', methodologyText);
        console.log(`Registered methodology data for ${this.displayType}:`, textContent.substring(0, 100) + '...');
      } else {
        console.log(`Skipping methodology registration for ${this.displayType} - incomplete data`);
      }
    },
  },
};
</script>